import pino from "pino";

// Extend the logger type to include custom methods
interface CustomLogger extends pino.Logger {
  highVisDebug: (obj: any, msg?: string) => void;
}

export const log = pino({
  base: undefined,
  /**
   * Use write option since serialize is not working in middleware.
   * See: https://github.com/vercel/next.js/discussions/33898
   */
  browser: {
    write(obj) {
      try {
        console.log(JSON.stringify(obj));
      } catch (err) {
        if (err instanceof Error) {
          // Without a `replacer` argument, stringify on Error results in `{}`
          console.log(JSON.stringify(err, ["name", "message", "stack"]));
        }

        console.log(JSON.stringify({ message: "Unknown error type" }));
      }
    },
  },
  level:
    process.env.LOG_LEVEL || process.env.DEBUG_ENABLED === "true"
      ? "debug"
      : "info",
  customLevels: {
    highVisDebug: 20, // Same as debug level
  },
}) as unknown as CustomLogger;

/**
 * Helper function to log arrows and content with consistent formatting
 */
function logArrowBlock(content: string, includeData?: string) {
  const arrowPrefix = `${logColors.red}--------->`;
  const arrow = `${arrowPrefix} ${logColors.reset}`;

  log.debug({}, arrow);
  log.debug({}, `${arrowPrefix} ${content}${logColors.reset}`);
  log.debug({}, arrow);

  if (includeData) {
    log.debug(includeData);
  }
}

// Add the custom method to the logger
log.highVisDebug = (obj: any, msg?: string) => {
  if (typeof obj === "string") {
    logArrowBlock(obj);
  } else if (msg) {
    logArrowBlock(msg, JSON.stringify(obj, null, 2));
  } else {
    logArrowBlock("HIGH VIS DEBUG", JSON.stringify(obj, null, 2));
  }
};

export const logColors: { [index: string]: string } = {
  yellow: "\x1b[33m",
  red: "\x1b[31m",
  white: "\x1b[37m",
  magenta: "\x1b[35m",
  green: "\x1b[32m",
  blue: "\x1b[34m",
  cyan: "\x1b[36m",
  reset: "\x1b[0m",
  none: "",
};

// Shared type for log options
type LogOptions = {
  /**
   * The message to log.
   * Can be a string, number, boolean, object, null, or undefined.
   */
  message: string | number | boolean | object | null | undefined;
  // The color of the log message.
  color?:
    | "yellow"
    | "red"
    | "white"
    | "green"
    | "blue"
    | "cyan"
    | "magenta"
    | "reset"
    | "none"
    | null;
  // The log level of the message.
  logLevel?: "log" | "warn" | "debug" | "info" | "error";
  // When the message is an object dictates whether it is logged as an object or JSON stringified.
  asObject?: boolean;
};

/**
 * Logs a message with optional color and log level.
 *
 * @param message The message to log. Can be a string, number, boolean, object, null, or undefined.
 * @param color The color of the log message. Defaults to null, which will colorize based on logLevel.
 * @param logLevel The log level of the message. Defaults to "log".
 */
export const colorizeLog = ({
  message,
  color,
  logLevel = "log",
  asObject = true,
}: LogOptions) => {
  const levelColorMap = {
    log: "green",
    warn: "yellow",
    error: "red",
    debug: "magenta",
    info: "none",
  };

  const isObject = typeof message === "object";
  const setColor = logColors[color || levelColorMap[logLevel]] || "";
  const resetColor = logColors.reset || "";

  if (isObject && asObject) {
    console.log(setColor);
    console[logLevel](message);
    console.log(resetColor);
    return;
  }

  const messageString = isObject
    ? JSON.stringify(message, null, 2)
    : message || "";

  console[logLevel](`${setColor}${messageString}${resetColor}`);
};

/**
 * Logs a message with optional color and log level if debug is enabled.
 * Debug is enabled if DEBUG_ENABLED environment variable is "true"
 * or if any of the environment debug enablers in envDebugEnablers are set to "true".
 *
 * @param message The message to log. Can be a string, number, boolean, object, null, or undefined.
 * @param color The color of the log message. Defaults to "red".
 * @param logLevel The log level of the message. Defaults to "debug".
 * @param envDebugEnablers An array of environment debug enablers to check. Defaults to an empty array.
 */
export const debugEnabledLog = ({
  message,
  color = null,
  logLevel = "debug",
  envDebugEnablers = [],
}: LogOptions & { envDebugEnablers?: string[] }) => {
  if (
    process.env.DEBUG_ENABLED === "true" ||
    envDebugEnablers.map((env: string) => process.env[env]).includes("true")
  ) {
    colorizeLog({ message, color, logLevel });
  }
};

export const isomorphicTimer = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

// Helper function to get execution time depending on the environment
const getExecutionTime = (start: number): number => {
  /**
   * Check it the performance API is available in the environment. Client side
   * the browser performance API is available. Server side performance can
   * be important from the Node.js `perf_hooks` module.
   */
  if (typeof performance !== "undefined") {
    return performance.now() - start;
  } else {
    /**
     * This is a server-side environment without the performance API imported.
     */
    return Date.now() - start;
  }
};

/**
 * Util intended for retrieving execution time for performance analysis as a variable
 *
 * e.g.
 *
 * const myTimer = performance.now();
 * timeConsumingFunction();
 * getExecutionDuration(myTimer);
 */
export const getExecutionDuration = (
  start: number,
  timeMeasurement: "ms" | "s" = "ms",
): string => {
  const executionTime = getExecutionTime(start);
  const inSeconds = timeMeasurement === "s";

  return (inSeconds ? executionTime / 1000 : executionTime).toFixed(
    inSeconds ? 2 : 0,
  );
};

/**
 * Util intended for logging execution time for performance analysis
 *
 * e.g.
 *
 * const myTimer = performance.now();
 * timeConsumingFunction();
 * logPerformance(myTimer, "Time taken to execute timeConsumingFunction: ", "green");
 */
export const logPerformance = (
  start: number,
  message = "",
  color: "yellow" | "red" | "green" | "blue" | "reset" | "none" = "red",
) => {
  const executionTime = getExecutionTime(start);
  let timeString;

  if (executionTime > 1000) {
    timeString = `${(executionTime / 1000).toFixed(2)}s`;
  } else {
    timeString = `${executionTime.toFixed(2)}ms`;
  }

  // Check if the environment is a browser
  const isBrowser = typeof window !== "undefined";

  if (isBrowser) {
    console.log(`%c${message}${timeString}`, `color: ${color};`);
  } else {
    console.log(
      `${logColors[color] || ""}${message}${timeString}${
        logColors.reset || ""
      }`,
    );
  }

  return executionTime.toFixed(2);
};

/**
 * Returns logger bindings with consistent network context across all packages.
 * Automatically includes network information from environment variables when available.
 *
 * @param name - The name identifier for the logger (e.g., "duet-framework", "duet-cli")
 * @param additionalContext - Optional additional context to include in the bindings
 * @returns An object with name, network context, and additional context for logger.child()
 *
 * @example
 * Basic usage
 * const log = rootLogger.child(getLoggerBindings("duet-framework"));
 *
 * With additional context
 * const log = rootLogger.child(getLoggerBindings("duet-cli", { version: "1.0.0" }));
 */
export const getLoggerBindings = (
  name: string,
  additionalContext?: Record<string, any>,
): pino.Bindings => {
  const networkId = process.env.NEXT_PUBLIC_NETWORK_ID;
  const networkName = process.env.NEXT_PUBLIC_NETWORK_NAME;
  const networkData = [networkId, networkName].filter(Boolean);

  return {
    name,
    // Add network context if either field is defined
    ...(networkData.length > 0 && {
      network: {
        // Add networkId only if defined
        ...(networkId && {
          networkId,
        }),
        // Add networkName only if defined
        ...(networkName && {
          networkName,
        }),
      },
    }),
    // Spread any additional context provided
    ...additionalContext,
  };
};
